import * as Sentry from "@sentry/vue";
import axios from "axios";
import { BootstrapVue, BootstrapVueIcons } from "bootstrap-vue";

import Vue from "vue";
import VueAxios from "vue-axios";
import VueGtag from "vue-gtag";
import vueHeadful from "vue-headful";
import VueScrollTo from "vue-scrollto";
import VueTheMask from "vue-the-mask";

import App from "./App.vue";
import LayoutFooter from "./components/Layout/Footer.vue";

import LayoutHeader from "./components/Layout/Header.vue";
import LayoutLoading from "./components/Layout/Loading.vue";
import LayoutPage from "./components/Layout/Page.vue";

import { filters } from "./helpers";
import dayjs from "./helpers/dayjs";

import "@/assets/scss/main.scss";
import i18n from "./i18n";
import router from "./router";
import store from "./store";
import { ValidationObserver, ValidationProvider } from "./vee-validate";
import "vue-cal/dist/vuecal.css";

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    Vue,
    release: process.env.VUE_APP_VERSION,
    environment: process.env.VUE_APP_STRIPE_ENV || process.env.NODE_ENV,
    dsn: "https://57e501b1bd34cedaeb6bfeb92590e67d@sentry.echosvirtuels.com/14",
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
      new Sentry.Replay(),
    ],
    tracesSampleRate: 0.01,
    // Only capture replays for errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

Vue.config.productionTip = false;

Vue.use(VueTheMask);
Vue.use(VueScrollTo, { duration: 500, offset: -20 });
Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);

// Google Analytics configuration
if (process.env.VUE_APP_GA_MEASUREMENT_ID) {
  Vue.use(
    VueGtag,
    {
      config: { id: process.env.VUE_APP_GA_MEASUREMENT_ID },
    },
    router
  );
}

// Google Tag Manager configuration
if (process.env.VUE_APP_GTM_MEASUREMENT_ID) {
  Vue.use(
    VueGtag,
    {
      config: { id: process.env.VUE_APP_GTM_MEASUREMENT_ID },
    },
    router
  );
}

Vue.component("LayoutFooter", LayoutFooter);
Vue.component("LayoutHeader", LayoutHeader);
Vue.component("LayoutLoading", LayoutLoading);
Vue.component("LayoutPage", LayoutPage);

Vue.component("ValidationObserver", ValidationObserver);
Vue.component("ValidationProvider", ValidationProvider);

Vue.component("VueHeadful", vueHeadful);

Object.keys(filters).forEach((f) => Vue.filter(f, (filters as any)[f]));
Vue.prototype.$filters = filters;

axios.defaults.baseURL = `/api/v1`;
axios.interceptors.request.use((config) => {
  if (store.state.token) {
    // eslint-disable-next-line
    config.headers.Authorization = `Bearer ${store.state.token}`;
  }
  return config;
});

axios.interceptors.response.use((response) => {
  // set client version in store from header
  if (
    response.headers &&
    typeof response.headers === "object" &&
    Object.prototype.hasOwnProperty.call(response.headers, "x-client-version")
  ) {
    store.commit("setClientVersionFromBackend", response.headers["x-client-version"]);
  }
  return response;
});

Vue.use(VueAxios, axios);

/**
 * Global clock. This allows us to use `this.$second` in any vue component
 * either as a watch or a reactive property to depend on in computed attributes.
 *
 * Using this ensures all time-based UI (disabling buttons before time etc.) updates
 * at the same time.
 */
const clock = Vue.observable({ second: dayjs() });
Object.defineProperties(Vue.prototype, {
  $dayjs: {
    get() {
      return dayjs;
    },
  },
  $second: {
    get() {
      return clock.second;
    },
    set(value) {
      clock.second = value;
    },
  },
});
Vue.dayjs = dayjs;

window.setInterval(() => {
  Vue.prototype.$second = dayjs();
}, 1000);

// Allow indirect reference to store and router through the Vue instance.
// This allows use of these in helper modules without creating a dependency on the router
// which itself essentially depends on all views. This is meaningful mainly for tests, where
// we might not want to load the whole app to test a single component.
// At time of creation, these indirect references are used in the requests/server.js file
// which is pulled in many views and components.
Vue.$store = store;
Vue.$router = router;

export const app = new Vue({
  i18n,
  render: (h) => h(App),
  router,
  store,
});

app.$mount("#app");
